import { useEffect, useMemo, useState } from "react"
import {
  UserNotification,
  subscribeToUserNotifications,
  markNotificationRead,
  markNotificationsRead
} from "@/firebase/firestore/userNotifications"

export const useUserNotifications = (userId: string | null | undefined, options?: { limit?: number }) => {
  const [notifications, setNotifications] = useState<UserNotification[]>([])
  const limit = options?.limit
  useEffect(() => {
    if (!userId) {
      // Reset immediately rather than waiting for a subscription callback that will never
      // come - intentional direct setState, not derivable at render time (would otherwise
      // flash stale notifications from a previous userId).
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setNotifications([])
      return
    }
    const unsubscribe = subscribeToUserNotifications(
      userId,
      (items) => {
        setNotifications(items)
      },
      { limit }
    )
    return () => unsubscribe()
  }, [userId, limit])

  const unreadCount = useMemo(() => notifications.filter((notification) => !notification.read).length, [notifications])

  const markAsRead = async (notificationId: string) => {
    await markNotificationRead(notificationId)
  }

  const markAllAsRead = async () => {
    const unreadIds = notifications.filter((notification) => !notification.read).map((notification) => notification.id)
    await markNotificationsRead(unreadIds)
  }

  return { notifications, unreadCount, markAsRead, markAllAsRead }
}

export type { UserNotification } from "@/firebase/firestore/userNotifications"
